import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import {
  Clock,
  Film,
  Headphones,
  Image as ImageIcon,
  FileText,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';
import JubileeVideoPlayerModal from '../viewers/JubileeVideoPlayerModal';
import JubileeImageLightbox from '../viewers/JubileeImageLightbox';
import JubileeDocumentViewerModal from '../viewers/JubileeDocumentViewerModal';

export default function JubileeTimelineBrowse({ onPlayAudioTrack }) {
  const [selectedDecade, setSelectedDecade] = useState('2026');
  const [selectedYear, setSelectedYear] = useState('all');

  // Modals state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);

  const decades = useMemo(() => jubileeStorageService.getTimelineDecades(), []);
  const allMedia = useMemo(() => jubileeStorageService.getAllMedia(), []);

  // Filter media for the selected decade/year
  const decadeMedia = useMemo(() => {
    let items = allMedia;

    if (selectedDecade === '2026') {
      items = items.filter((m) => String(m.year) === '2026');
    } else {
      const startYear = parseInt(selectedDecade.substring(0, 4), 10);
      const endYear = startYear + 9;
      items = items.filter((m) => {
        const y = parseInt(m.year, 10);
        return y >= startYear && y <= endYear;
      });
    }

    if (selectedYear !== 'all') {
      items = items.filter((m) => String(m.year) === String(selectedYear));
    }

    return {
      all: items,
      videos: items.filter((m) => m.mediaType === 'video'),
      audios: items.filter((m) => m.mediaType === 'audio'),
      images: items.filter((m) => m.mediaType === 'image'),
      documents: items.filter((m) => m.mediaType === 'document'),
    };
  }, [allMedia, selectedDecade, selectedYear]);

  // Unique years in current decade
  const uniqueYearsInDecade = Array.from(
    new Set(
      allMedia
        .filter((m) => {
          if (selectedDecade === '2026') return String(m.year) === '2026';
          const start = parseInt(selectedDecade.substring(0, 4), 10);
          return parseInt(m.year, 10) >= start && parseInt(m.year, 10) <= start + 9;
        })
        .map((m) => m.year)
    )
  ).sort((a, b) => b - a);

  const currentDecadeInfo = decades.find((d) => d.decade === selectedDecade) || decades[0];

  return (
    <div className="py-4">
      <Container>
        {/* Timeline Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="p-2 rounded-3 text-white d-inline-flex" style={{ background: '#d4af37' }}>
                <Clock size={22} />
              </div>
              <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                75-Year Chronological Timeline (1951 - 2026)
              </h2>
            </div>
            <p className="text-muted small m-0">
              Journey through the 75-year history of the Madurai Jesuit Province decade by decade.
            </p>
          </div>

          <span className="badge bg-warning bg-opacity-25 text-dark border border-warning border-opacity-50 px-3 py-2 rounded-pill fw-bold">
            Decade: {currentDecadeInfo.range}
          </span>
        </div>

        {/* Decade Navigation Pills */}
        <div className="timeline-decade-nav">
          {decades.map((dec) => (
            <button
              key={dec.decade}
              className={`timeline-decade-btn ${selectedDecade === dec.decade ? 'active' : ''}`}
              onClick={() => {
                setSelectedDecade(dec.decade);
                setSelectedYear('all');
              }}
            >
              {dec.decade === '2026' ? '✨ 2026 Jubilee Year' : dec.decade}
            </button>
          ))}
        </div>

        {/* Decade Summary Card */}
        <div className="bg-white p-4 rounded-4 shadow-sm border mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="badge bg-success">{currentDecadeInfo.range}</span>
                <span className="text-muted small">• Historical Overview</span>
              </div>
              <h4 className="fw-bold text-slate-900 m-0">{currentDecadeInfo.summary}</h4>
            </div>

            {/* Year Sub-filters */}
            {uniqueYearsInDecade.length > 1 && (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="text-muted small fw-semibold">Filter Year:</span>
                <button
                  className={`btn btn-sm ${selectedYear === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setSelectedYear('all')}
                >
                  All Years
                </button>
                {uniqueYearsInDecade.map((yr) => (
                  <button
                    key={yr}
                    className={`btn btn-sm ${selectedYear === yr ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setSelectedYear(yr)}
                  >
                    {yr}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Timeline Media Pillars for this Decade */}
        <div className="row g-4">
          {/* DOCUMENTS IN THIS ERA */}
          <div className="col-12 col-lg-6">
            <div className="bg-white p-4 rounded-4 border shadow-sm h-100">
              <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                <div className="d-flex align-items-center gap-2">
                  <FileText className="text-success" size={20} />
                  <h6 className="fw-bold m-0 text-slate-900">Documents ({decadeMedia.documents.length})</h6>
                </div>
              </div>

              {decadeMedia.documents.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {decadeMedia.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="p-3 rounded-3 border bg-light d-flex align-items-center justify-content-between cursor-pointer hover-shadow"
                      onClick={() => {
                        setSelectedDoc(doc);
                        setShowDocModal(true);
                      }}
                    >
                      <div className="overflow-hidden pe-2">
                        <div className="badge bg-success bg-opacity-10 text-success me-2">{doc.year}</div>
                        <span className="fw-semibold text-dark small">{doc.title}</span>
                        <div className="text-muted text-xs text-truncate">{doc.description}</div>
                      </div>
                      <Button variant="outline-success" size="sm" className="rounded-pill flex-shrink-0">
                        Read
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small m-0">No documents catalogued under this era yet.</p>
              )}
            </div>
          </div>

          {/* PHOTOGRAPHS IN THIS ERA */}
          <div className="col-12 col-lg-6">
            <div className="bg-white p-4 rounded-4 border shadow-sm h-100">
              <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                <div className="d-flex align-items-center gap-2">
                  <ImageIcon className="text-warning" size={20} />
                  <h6 className="fw-bold m-0 text-slate-900">Photographs ({decadeMedia.images.length})</h6>
                </div>
              </div>

              {decadeMedia.images.length > 0 ? (
                <Row className="g-2">
                  {decadeMedia.images.map((img, idx) => (
                    <Col key={img.id} xs={6} sm={4}>
                      <div
                        className="rounded overflow-hidden position-relative cursor-pointer"
                        style={{ height: '110px' }}
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setShowImageModal(true);
                        }}
                      >
                        <img src={img.thumbnailUrl || img.fileUrl} alt={img.title} className="w-100 h-100 object-fit-cover" />
                        <span className="position-absolute bottom-0 start-0 end-0 p-1 text-xs text-white bg-dark bg-opacity-75 text-truncate">
                          {img.year} • {img.title}
                        </span>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <p className="text-muted small m-0">No photographs catalogued under this era yet.</p>
              )}
            </div>
          </div>

          {/* AUDIO & SPEECHES IN THIS ERA */}
          <div className="col-12 col-lg-6">
            <div className="bg-white p-4 rounded-4 border shadow-sm h-100">
              <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                <div className="d-flex align-items-center gap-2">
                  <Headphones className="text-primary" size={20} />
                  <h6 className="fw-bold m-0 text-slate-900">Audio & Speeches ({decadeMedia.audios.length})</h6>
                </div>
              </div>

              {decadeMedia.audios.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {decadeMedia.audios.map((aud) => (
                    <div
                      key={aud.id}
                      className="p-3 rounded-3 border bg-light d-flex align-items-center justify-content-between"
                    >
                      <div>
                        <span className="badge bg-primary bg-opacity-10 text-primary me-2">{aud.year}</span>
                        <span className="fw-semibold text-dark small">{aud.title}</span>
                        <div className="text-muted text-xs">Speaker: {aud.speaker || 'Archival Record'}</div>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        className="rounded-pill flex-shrink-0"
                        onClick={() => onPlayAudioTrack && onPlayAudioTrack(aud)}
                      >
                        Listen
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small m-0">No audio recordings catalogued under this era yet.</p>
              )}
            </div>
          </div>

          {/* VIDEOS IN THIS ERA */}
          <div className="col-12 col-lg-6">
            <div className="bg-white p-4 rounded-4 border shadow-sm h-100">
              <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                <div className="d-flex align-items-center gap-2">
                  <Film className="text-danger" size={20} />
                  <h6 className="fw-bold m-0 text-slate-900">Videos & Films ({decadeMedia.videos.length})</h6>
                </div>
              </div>

              {decadeMedia.videos.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {decadeMedia.videos.map((vid) => (
                    <div
                      key={vid.id}
                      className="p-3 rounded-3 border bg-light d-flex align-items-center justify-content-between cursor-pointer"
                      onClick={() => {
                        setSelectedVideo(vid);
                        setShowVideoModal(true);
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <img src={vid.thumbnailUrl} alt={vid.title} className="rounded" style={{ width: '50px', height: '36px', objectFit: 'cover' }} />
                        <div>
                          <span className="badge bg-danger bg-opacity-10 text-danger me-2">{vid.year}</span>
                          <span className="fw-semibold text-dark small">{vid.title}</span>
                        </div>
                      </div>
                      <Button variant="outline-danger" size="sm" className="rounded-pill flex-shrink-0">
                        Watch
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small m-0">No video records catalogued under this era.</p>
              )}
            </div>
          </div>
        </div>
      </Container>

      {/* Media Preview Modals */}
      <JubileeVideoPlayerModal
        video={selectedVideo}
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        allVideos={allMedia.filter((m) => m.mediaType === 'video')}
      />

      <JubileeImageLightbox
        images={decadeMedia.images}
        selectedIndex={selectedImageIndex}
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelectIndex={(i) => setSelectedImageIndex(i)}
      />

      <JubileeDocumentViewerModal
        document={selectedDoc}
        show={showDocModal}
        onHide={() => setShowDocModal(false)}
      />
    </div>
  );
}
